// src/pages/KarigarManagement/Payment/PendingPaymentsPage.jsx
// Fixed: processPayment signature, proper bulk process handling

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePaymentManagement } from "@/hooks/useKarigar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui-v2/table";
import { Button } from "@/components/ui-v2/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui-v2/card";
import { Checkbox } from "@/components/ui-v2/checkbox";
import { Badge } from "@/components/ui-v2/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui-v2/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui-v2/select";
import { Label } from "@/components/ui-v2/label";
import { Input } from "@/components/ui-v2/input";
import { Textarea } from "@/components/ui-v2/textarea";
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  IndianRupee,
  AlertTriangle,
  Loader2,
  RefreshCw,
} from "lucide-react";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { toast } from "sonner";
import { format } from "date-fns";

const PAYMENT_METHODS = [
  { value: "CASH", label: "Cash" },
  { value: "BANK_TRANSFER", label: "Bank Transfer" },
  { value: "UPI", label: "UPI" },
  { value: "CHEQUE", label: "Cheque" },
  { value: "NEFT", label: "NEFT" },
  { value: "RTGS", label: "RTGS" },
  { value: "IMPS", label: "IMPS" },
];

const PendingPaymentsPage = () => {
  const navigate = useNavigate();

  const {
    pendingPayments,
    pendingCount,
    pendingTotal,
    loading,
    error,
    successMessage,
    getPendingPayments,
    processPayment,
    bulkProcess, // ✅ Use bulkProcess for multiple payments
    clearError,
    clearSuccessMessage,
  } = usePaymentManagement();

  const [selectedPayments, setSelectedPayments] = useState([]);
  const [showBulkProcessDialog, setShowBulkProcessDialog] = useState(false);
  const [showSingleProcessDialog, setShowSingleProcessDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  // Form state
  const [bulkMethod, setBulkMethod] = useState("BANK_TRANSFER");
  const [bulkNotes, setBulkNotes] = useState("");
  const [singleMethod, setSingleMethod] = useState("BANK_TRANSFER");
  const [singleTransactionId, setSingleTransactionId] = useState("");
  const [singleReferenceNumber, setSingleReferenceNumber] = useState("");
  const [singleNotes, setSingleNotes] = useState("");

  const [processing, setProcessing] = useState(false);

  // Fetch pending payments
  useEffect(() => {
    getPendingPayments();
  }, []);

  // Handle messages
  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      clearSuccessMessage();
      getPendingPayments(); // Refresh
      setSelectedPayments([]); // Clear selection
    }
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [successMessage, error]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Handle select all
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedPayments(pendingPayments.map((p) => p._id));
    } else {
      setSelectedPayments([]);
    }
  };

  // Handle individual select
  const handleSelect = (paymentId, checked) => {
    if (checked) {
      setSelectedPayments([...selectedPayments, paymentId]);
    } else {
      setSelectedPayments(selectedPayments.filter((id) => id !== paymentId));
    }
  };

  // Calculate selected total
  const selectedTotal = pendingPayments
    .filter((p) => selectedPayments.includes(p._id))
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  // ✅ Fixed: Handle bulk process using the bulkProcess function
  const handleBulkProcess = async () => {
    if (selectedPayments.length === 0) {
      toast.error("Please select payments to process");
      return;
    }

    setProcessing(true);
    try {
      // Use bulkProcess which handles multiple payments at once
      await bulkProcess(selectedPayments, bulkMethod, bulkNotes || undefined);
      setShowBulkProcessDialog(false);
      setBulkMethod("BANK_TRANSFER");
      setBulkNotes("");
    } catch (err) {
      // Error is handled by useEffect
    } finally {
      setProcessing(false);
    }
  };

  // Open single process dialog
  const openSingleProcessDialog = (payment) => {
    setSelectedPayment(payment);
    setSingleMethod("BANK_TRANSFER");
    setSingleTransactionId("");
    setSingleReferenceNumber("");
    setSingleNotes("");
    setShowSingleProcessDialog(true);
  };

  // ✅ Fixed: Handle single process with correct signature
  const handleSingleProcess = async () => {
    if (!selectedPayment) return;

    setProcessing(true);
    try {
      // processPayment(paymentId, paymentMethod, transactionId, referenceNumber, notes)
      await processPayment(
        selectedPayment._id,
        singleMethod,
        singleTransactionId || undefined,
        singleReferenceNumber || undefined,
        singleNotes || undefined
      );
      setShowSingleProcessDialog(false);
      setSelectedPayment(null);
    } catch (err) {
      // Error is handled by useEffect
    } finally {
      setProcessing(false);
    }
  };

  // Quick process (with default method)
  const handleQuickProcess = async (payment) => {
    setProcessing(true);
    try {
      await processPayment(
        payment._id,
        "BANK_TRANSFER",
        undefined,
        undefined,
        undefined
      );
    } catch (err) {
      // Error handled by useEffect
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Pending Payments</h1>
            <p className="text-muted-foreground">
              Process karigar payments awaiting approval
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => getPendingPayments()}>
            <RefreshCw
              className={`h-4 w-4 mr-2 ${
                loading.pending ? "animate-spin" : ""
              }`}
            />
            Refresh
          </Button>
          {selectedPayments.length > 0 && (
            <Button onClick={() => setShowBulkProcessDialog(true)}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Process Selected ({selectedPayments.length})
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Count</p>
                <p className="text-2xl font-bold">
                  {pendingCount || pendingPayments?.length || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                <IndianRupee className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Pending</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(
                    pendingTotal ||
                      pendingPayments?.reduce(
                        (sum, p) => sum + (p.amount || 0),
                        0
                      )
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={selectedPayments.length > 0 ? "border-primary" : ""}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Selected Amount</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(selectedTotal)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payments Table */}
      <Card>
        <CardContent className="p-0">
          {loading.pending ? (
            <div className="flex justify-center items-center h-64">
              <LoadingSpinner />
            </div>
          ) : !pendingPayments || pendingPayments.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <CheckCircle className="h-12 w-12 mb-4 text-green-500" />
              <p className="text-lg font-medium">All caught up!</p>
              <p>No pending payments to process</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={
                        selectedPayments.length === pendingPayments.length &&
                        pendingPayments.length > 0
                      }
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Karigar</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingPayments.map((payment) => (
                  <TableRow key={payment._id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedPayments.includes(payment._id)}
                        onCheckedChange={(checked) =>
                          handleSelect(payment._id, checked)
                        }
                      />
                    </TableCell>

                    {/* Payment Info */}
                    <TableCell>
                      <div>
                        <p className="font-medium">
                          {payment.paymentNumber || payment._id?.slice(-8)}
                        </p>
                        <Badge variant="outline" className="text-xs">
                          {payment.paymentType?.replace(/_/g, " ")}
                        </Badge>
                      </div>
                    </TableCell>

                    {/* Karigar */}
                    <TableCell>
                      <div>
                        <p className="font-medium">
                          {payment.karigar?.name ||
                            payment.karigarId?.name ||
                            "-"}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {payment.karigar?.karigarCode ||
                            payment.karigarId?.karigarCode ||
                            ""}
                        </p>
                      </div>
                    </TableCell>

                    {/* Job */}
                    <TableCell>
                      {payment.job || payment.jobId ? (
                        <p className="text-sm">
                          {payment.job?.jobNumber ||
                            payment.jobId?.jobNumber ||
                            "-"}
                        </p>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>

                    {/* Amount */}
                    <TableCell>
                      <p className="font-semibold text-primary">
                        {formatCurrency(
                          payment.amount ||
                            payment.grossAmount ||
                            payment.netAmount
                        )}
                      </p>
                    </TableCell>

                    {/* Created Date */}
                    <TableCell>
                      {payment.createdAt
                        ? format(new Date(payment.createdAt), "dd MMM yyyy")
                        : "-"}
                    </TableCell>

                    {/* Actions */}
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            navigate(
                              `/karigar-management/payments/${payment._id}`
                            )
                          }
                        >
                          View
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => openSingleProcessDialog(payment)}
                          disabled={processing}
                        >
                          Process
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Bulk Process Dialog */}
      <AlertDialog
        open={showBulkProcessDialog}
        onOpenChange={setShowBulkProcessDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Process Multiple Payments</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to process {selectedPayments.length} payments
              totaling {formatCurrency(selectedTotal)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Payment Method *</Label>
              <Select value={bulkMethod} onValueChange={setBulkMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((method) => (
                    <SelectItem key={method.value} value={method.value}>
                      {method.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                placeholder="Notes for all payments..."
                value={bulkNotes}
                onChange={(e) => setBulkNotes(e.target.value)}
              />
            </div>

            <div className="p-3 bg-yellow-50 rounded flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <p className="text-sm text-yellow-700">
                Make sure you have sufficient funds and all payment details are
                correct before proceeding.
              </p>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkProcess}
              disabled={processing}
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                `Process ${selectedPayments.length} Payments`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Single Process Dialog */}
      <AlertDialog
        open={showSingleProcessDialog}
        onOpenChange={setShowSingleProcessDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Process Payment</AlertDialogTitle>
            <AlertDialogDescription>
              Process payment of {formatCurrency(selectedPayment?.amount)} to{" "}
              {selectedPayment?.karigar?.name ||
                selectedPayment?.karigarId?.name ||
                "karigar"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Payment Method *</Label>
              <Select value={singleMethod} onValueChange={setSingleMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((method) => (
                    <SelectItem key={method.value} value={method.value}>
                      {method.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Transaction ID (Optional)</Label>
              <Input
                placeholder="TXN123456789"
                value={singleTransactionId}
                onChange={(e) => setSingleTransactionId(e.target.value)}
              />
            </div>

            <div>
              <Label>Reference Number (Optional)</Label>
              <Input
                placeholder="REF001"
                value={singleReferenceNumber}
                onChange={(e) => setSingleReferenceNumber(e.target.value)}
              />
            </div>

            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                placeholder="Payment notes..."
                value={singleNotes}
                onChange={(e) => setSingleNotes(e.target.value)}
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSingleProcess}
              disabled={processing}
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                "Confirm Payment"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PendingPaymentsPage;
